import { useState, useCallback } from 'react';
import { socialProofApi } from '../api/socialProof';
import type { SocialProofSettings, SocialProofEvent } from '../api/socialProof';

export function useSocialProof() {
  const [settings, setSettings] = useState<SocialProofSettings | null>(null);
  const [events, setEvents] = useState<SocialProofEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLastPage, setEventsLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await socialProofApi.show();
      setSettings(res.data.data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load social proof settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (next: SocialProofSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await socialProofApi.update(next);
      setSettings(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save social proof settings.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const fetchEvents = useCallback(async (page = 1) => {
    try {
      const res = await socialProofApi.events(page);
      setEvents(res.data.data);
      setEventsTotal(res.data.meta.total);
      setEventsLastPage(res.data.meta.last_page);
      setEventsPage(page);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load recent events.');
    }
  }, []);

  return {
    settings, events, eventsTotal, eventsPage, eventsLastPage,
    loading, saving, error, fetchSettings, save, fetchEvents, setError,
  };
}
